import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Send, MessageSquare, Clock, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { MarketingShell } from '@/seo/kit';

const SUBTLE = '#5B6678';
const BORDER = '#D8DEE8';

function Contact() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiService.submitContactForm(formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <MarketingShell>
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-24 text-center">
          <div className="w-14 h-14 border rounded-md flex items-center justify-center mx-auto mb-6" style={{ borderColor: BORDER }}>
            <CheckCircle2 className="w-7 h-7" style={{ color: '#1F7A4F' }} />
          </div>
          <h1 className="text-3xl sm:text-4xl">Message Sent</h1>
          <p className="text-lg mt-4" style={{ color: SUBTLE }}>
            Thank you for reaching out. We'll get back to you within 24 hours.
          </p>
          <div className="flex justify-center gap-3 mt-8">
            <button className="btn-secondary" onClick={() => navigate('/')} data-testid="contact-back-home">Back to Home</button>
            <button
              className="btn-primary"
              data-testid="contact-send-another"
              onClick={() => { setIsSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }}
            >
              Send another message
            </button>
          </div>
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left — info */}
          <div>
            <h1 className="text-4xl sm:text-5xl">Get in touch</h1>
            <p className="text-lg mt-5 max-w-xl" style={{ color: SUBTLE }}>
              Have questions about DrawReady? We're here to help. Send us a message and we'll respond within 24 hours.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex gap-4">
                <div className="w-11 h-11 border rounded-md flex items-center justify-center shrink-0" style={{ borderColor: BORDER }}>
                  <Clock className="w-5 h-5" style={{ color: '#1E4ED8' }} />
                </div>
                <div>
                  <h3 className="font-semibold" style={{ color: '#13294B' }}>Quick response</h3>
                  <p style={{ color: SUBTLE }}>We respond to all inquiries within 24 hours during business days.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 border rounded-md flex items-center justify-center shrink-0" style={{ borderColor: BORDER }}>
                  <MessageSquare className="w-5 h-5" style={{ color: '#1E4ED8' }} />
                </div>
                <div>
                  <h3 className="font-semibold" style={{ color: '#13294B' }}>Common questions</h3>
                  <p style={{ color: SUBTLE }}>
                    Check our <Link to="/pricing" className="font-medium" style={{ color: '#1E4ED8' }}>pricing page</Link> for plan details or <Link to="/terms" className="font-medium" style={{ color: '#1E4ED8' }}>terms</Link> for policies.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="border rounded-lg p-6 md:p-8" style={{ borderColor: BORDER }}>
            <h2 className="text-2xl mb-6">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" data-testid="contact-name" placeholder="Your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" data-testid="contact-email" placeholder="you@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input id="subject" data-testid="contact-subject" placeholder="How can we help?" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" data-testid="contact-message" placeholder="Tell us more about your question or feedback..." rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required />
              </div>
              <button type="submit" disabled={isSubmitting} data-testid="contact-submit" className="btn-primary w-full" style={{ textAlign: 'center' }}>
                {isSubmitting ? (
                  <span className="inline-flex items-center justify-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" /> Sending...</span>
                ) : (
                  <span className="inline-flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Send message</span>
                )}
              </button>
              <p className="text-xs text-center" style={{ color: SUBTLE }}>
                By submitting, you agree to our <Link to="/privacy" className="font-medium" style={{ color: '#1E4ED8' }}>Privacy Policy</Link>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </MarketingShell>
  );
}

export default Contact;
